import { fetchClients } from '@/features/clients/api/use-clients';
import type { AdminClient } from '@/shared/types/client';
import { fetchAllCampaigns } from '@/features/campaigns/api/use-campaigns';
import {
  mapLegacyCampaign,
  type LegacyCampaign,
} from '@/features/campaigns/model/campaign-mapper';
import {
  clearAgendaCache,
  readAgendaCache,
  writeAgendaCache,
} from '../model/agenda-cache';
import type { AgendaCampaign } from '../model/types';

export type { AgendaCampaign };
export { clearAgendaCache, readAgendaCache, agendaCacheAgeLabel } from '../model/agenda-cache';

export interface PassbookTarget {
  clientId: string;
  clientName: string;
  passbookId: string;
  backgroundColor?: string;
}

/**
 * Mantido só pela assinatura de scanFullAgendaCatalog (a UI antiga mostrava
 * progresso por loja). Agora a varredura é 1 chamada só -- done/total ficam
 * 0/0 até terminar e 1/1 no fim, sem passo intermediário real.
 */
export interface AgendaScanProgress {
  done: number;
  total: number;
  clientName: string;
  passbookId: string;
}

function mapAgendaCampaign(
  row: LegacyCampaign & { id_passbook?: string },
  target: PassbookTarget,
): AgendaCampaign {
  const base = mapLegacyCampaign(row);
  const passbookId = String(row.id_passbook ?? target.passbookId);
  return {
    ...base,
    id_passbook: passbookId,
    clientId: target.clientId,
    clientName: target.clientName,
    passbookId,
    passbookName: target.clientName,
    backgroundColor: target.backgroundColor,
    expirar: row.expirar,
    dt_fim: row.dt_fim ? String(row.dt_fim) : undefined,
  };
}

/** Todas as lojas (exceto onlyShortUrl). */
export function buildPassbookTargets(clients: AdminClient[]): PassbookTarget[] {
  const targets: PassbookTarget[] = [];
  for (const client of clients) {
    for (const pb of client.passbooks ?? []) {
      if (!pb._id || pb.onlyShortUrl) continue;
      targets.push({
        clientId: String(client._id),
        clientName: client.name || 'Cliente desconhecido',
        passbookId: String(pb._id),
        backgroundColor: pb.backgroundColor,
      });
    }
  }
  return targets;
}

export async function fetchAgendaClients(): Promise<AdminClient[]> {
  return fetchClients();
}

/**
 * Busca clientes + TODAS as campanhas em 2 chamadas paralelas (em vez de
 * ~500 requests sequenciais, 1 por loja, 1.2s de pausa cada) e junta com
 * nome/cor de loja em memória. O endpoint /campaigns_agenda faz a mesma
 * agregação que /campaigns/:id_passbook já fazia, só que pra todas as lojas
 * de uma vez -- ver controllers/campaign.js#listAgenda no legado.
 */
export async function scanFullAgendaCatalog(options: {
  signal?: AbortSignal;
  onProgress?: (p: AgendaScanProgress) => void;
  force?: boolean;
}): Promise<{ campaigns: AgendaCampaign[]; fetchedAt: number; fromCache: boolean }> {
  if (!options.force) {
    const cached = readAgendaCache();
    if (cached) {
      return { campaigns: cached.campaigns, fetchedAt: cached.fetchedAt, fromCache: true };
    }
  } else {
    clearAgendaCache();
  }

  const [clients, rows] = await Promise.all([fetchClients(), fetchAllCampaigns()]);
  if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const targets = buildPassbookTargets(clients);
  const targetByPassbook = new Map(targets.map((t) => [t.passbookId, t] as const));

  const campaigns: AgendaCampaign[] = [];
  for (const row of rows) {
    const passbookId = String((row as LegacyCampaign & { id_passbook?: string }).id_passbook ?? '');
    const target = targetByPassbook.get(passbookId);
    if (!target) continue; // loja onlyShortUrl ou sem client associado — mesma regra de antes
    campaigns.push(mapAgendaCampaign(row, target));
  }

  writeAgendaCache(campaigns);
  options.onProgress?.({ done: 1, total: 1, clientName: '', passbookId: '' });
  return { campaigns, fetchedAt: Date.now(), fromCache: false };
}

/** Detalhe de 1 cliente: usa o cache se existir; senão busca o catálogo completo (1 request) e filtra. */
export async function fetchAgendaCampaignsForClient(
  clientId: string,
): Promise<{ clientName: string; campaigns: AgendaCampaign[] }> {
  const cached = readAgendaCache();
  const all = cached?.campaigns ?? (await scanFullAgendaCatalog({})).campaigns;

  const campaigns = all.filter((c) => c.clientId === clientId);
  const clientName =
    campaigns[0]?.clientName ??
    (await fetchClients()).find((c) => String(c._id) === clientId)?.name ??
    'Cliente';
  return { clientName, campaigns };
}
